"use client";

/**
 * WAX Client Service
 *
 * Client-side WAX blockchain service for browser-based wallet operations.
 * Uses Wharfkit for modern wallet integration with support for:
 * - WAX Cloud Wallet (My Cloud Wallet)
 * - Anchor Wallet
 * - Wombat Wallet
 */

import { Session, SessionKit } from "@wharfkit/session";
import { WalletPluginCloudWallet } from "@wharfkit/wallet-plugin-cloudwallet";
import { WalletPluginAnchor } from "@wharfkit/wallet-plugin-anchor";
import { WalletPluginWombat } from "@wharfkit/wallet-plugin-wombat";
import { WebRenderer } from "@wharfkit/web-renderer";
import { TransactPluginAutoCorrect } from "@wharfkit/transact-plugin-autocorrect";
import { getWaxConfig } from "./config";
import { clientLogger } from "../client-logger";
import { WaxAccountCreationPlugin } from "./account-creation-plugin";
import type {
  WaxUserAccount,
  WaxLoginResult,
  CloudWalletOptions,
  WalletType,
  TransactionAction,
  TransactionResult,
} from "./types";

/**
 * Build the direct-mode mobile-app-connect config for the My Cloud Wallet
 * plugin. When users have the My Cloud Wallet mobile app installed, this
 * routes login/signing through the app (via deep link) instead of the web
 * popup, then the app returns to callbackUri. Users without the app fall
 * back to the web flow automatically — no downside to enabling it.
 */
function buildMobileAppConnectConfig() {
  if (typeof window === "undefined") return undefined;
  return {
    direct: {
      callbackUri: window.location.href,
      broadcastChannel: "mycloudwallet-auth",
    },
    dappInfo: {
      name: process.env.NEXT_PUBLIC_APP_NAME || "Scavenger Hunt",
      description: "Scavenger hunt participation & digital asset claims",
    },
  };
}

// Wallet plugin IDs as used by Wharfkit
const WALLET_PLUGIN_IDS: Record<WalletType, string> = {
  cloudwallet: "cloudwallet",
  anchor: "anchor",
  wombat: "wombat",
};

// Reverse mapping: plugin ID -> WalletType
const PLUGIN_ID_TO_WALLET_TYPE: Record<string, WalletType> = Object.entries(
  WALLET_PLUGIN_IDS
).reduce((acc, [walletType, pluginId]) => {
  acc[pluginId] = walletType as WalletType;
  return acc;
}, {} as Record<string, WalletType>);

export class WaxClientService {
  private sessionKit: SessionKit | null = null;
  private session: Session | null = null;
  private config = getWaxConfig();
  private isInitialized = false;
  private accountCreationPlugin: WaxAccountCreationPlugin | null = null;

  // ============================================================================
  // Initialization Methods
  // ============================================================================

  /**
   * Initialize the SessionKit for wallet authentication
   */
  async initialize(): Promise<boolean> {
    if (this.isInitialized && this.sessionKit) {
      return true;
    }

    try {
      if (typeof window === "undefined") {
        throw new Error("WaxClientService can only be used in browser environment");
      }

      clientLogger.info("Initializing SessionKit");

      // Always use mainnet Cloud Wallet - testnet version doesn't exist
      const baseCloudWalletUrl = "https://www.mycloudwallet.com";

      const cloudWalletPlugin = new WalletPluginCloudWallet({
        url: baseCloudWalletUrl,
        loginTimeout: 900000, // 15 minutes - increased for slow wallet creation
        supportedChains: [this.config.chainId],
        mobileAppConnectConfig: buildMobileAppConnectConfig(),
      });

      clientLogger.debug("Cloud Wallet configured", { url: baseCloudWalletUrl, network: this.config.network });

      const anchorPlugin = new WalletPluginAnchor();
      const wombatPlugin = new WalletPluginWombat();

      this.accountCreationPlugin = new WaxAccountCreationPlugin({
        onAccountCreated: (accountName, isTemp) => {
          clientLogger.info("Account created", { accountName, isTemp });
        },
        onError: (error) => {
          clientLogger.error("Account creation error", { error });
        },
      });

      this.sessionKit = new SessionKit({
        appName: "Scavenger Hunt SaaS",
        chains: [
          {
            id: this.config.chainId,
            url: this.config.rpcEndpoint,
          },
        ],
        ui: new WebRenderer(),
        walletPlugins: [cloudWalletPlugin, anchorPlugin, wombatPlugin],
      }, {
        transactPlugins: [new TransactPluginAutoCorrect()], // Auto-correct CPU/NET/RAM issues
      });

      await this.restoreSession();

      this.isInitialized = true;
      clientLogger.info("SessionKit initialized successfully");
      return true;
    } catch (error) {
      clientLogger.error("Error initializing SessionKit", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      this.isInitialized = false;
      return false;
    }
  }

  // ============================================================================
  // Session Management
  // ============================================================================

  /**
   * Restore previous session from storage
   */
  async restoreSession(): Promise<boolean> {
    if (!this.sessionKit) {
      return false;
    }

    try {
      const restored = await this.sessionKit.restore();
      if (restored) {
        this.session = restored;
        clientLogger.info("Session restored", { actor: restored.actor.toString() });
        return true;
      }
    } catch {
      clientLogger.debug("No existing session to restore");
    }

    return false;
  }

  /**
   * Check if auto-login is available
   */
  async isAutoLoginAvailable(): Promise<boolean> {
    if (!this.sessionKit) {
      await this.initialize();
    }
    return this.session !== null;
  }

  /**
   * Configure account creation plugin with user data
   */
  configureAccountCreation(email: string, firstName?: string, lastName?: string): void {
    if (this.accountCreationPlugin) {
      this.accountCreationPlugin.updateOptions({
        email,
        firstName,
        lastName,
      });
    }
  }

  /**
   * Logout current session
   */
  async logout(): Promise<void> {
    if (this.sessionKit && this.session) {
      try {
        await this.sessionKit.logout(this.session);
        this.session = null;
        clientLogger.info("Logged out successfully");
      } catch (error) {
        clientLogger.error("Logout error", {
          error: error instanceof Error ? error.message : "Unknown error"
        });
      }
    }
  }

  /**
   * Get current Wharfkit session
   */
  getCurrentSession(): Session | null {
    return this.session;
  }

  /**
   * Get current account info
   */
  getCurrentAccount(): WaxUserAccount | null {
    if (!this.session) {
      return null;
    }

    const accountName = this.session.actor.toString();
    const avatarBaseUrl =
      this.config.network === "testnet"
        ? "https://testnet.mycloudwallet.com"
        : "https://www.mycloudwallet.com";

    return {
      accountName,
      pubKeys: this.session.walletPlugin.metadata.publicKey
        ? [this.session.walletPlugin.metadata.publicKey]
        : [],
      avatar: `${avatarBaseUrl}/api/avatar/${accountName}`,
      isTemp: accountName.endsWith(".wam"),
      walletType: "cloudwallet",
    };
  }

  /**
   * Get current user account name if logged in
   */
  getCurrentUser(): string | null {
    if (!this.session) {
      return null;
    }
    return this.session.actor.toString();
  }

  // ============================================================================
  // Login Methods
  // ============================================================================

  /**
   * Helper to build login result from session response
   */
  private buildLoginResult(
    session: Session,
    options?: CloudWalletOptions
  ): WaxLoginResult {
    const accountName = session.actor.toString();
    const pluginId = session.walletPlugin?.id || "";
    const walletType = PLUGIN_ID_TO_WALLET_TYPE[pluginId] || "cloudwallet";
    const isTemp = accountName.endsWith(".wam");
    const avatarBaseUrl = "https://www.mycloudwallet.com";

    return {
      success: true,
      userAccount: {
        accountName,
        pubKeys: session.walletPlugin.metadata.publicKey
          ? [session.walletPlugin.metadata.publicKey]
          : [],
        avatar:
          walletType === "cloudwallet"
            ? `${avatarBaseUrl}/api/avatar/${accountName}`
            : `https://www.gravatar.com/avatar/${accountName}?d=identicon`,
        isTemp,
        email: options?.email,
        walletType,
      },
    };
  }

  /**
   * Create a new Cloud Wallet account (registration flow)
   * Uses the main SessionKit but specifies Cloud Wallet plugin directly
   */
  async createCloudWalletAccount(options?: CloudWalletOptions): Promise<WaxLoginResult> {
    if (!this.sessionKit) {
      await this.initialize();
    }

    if (!this.sessionKit) {
      return {
        success: false,
        error: "SessionKit not initialized",
      };
    }

    try {
      clientLogger.info("Starting Cloud Wallet account creation");
      clientLogger.debug("Options provided", { options });

      // Use Cloud Wallet plugin directly - skips wallet selection UI
      const response = await this.sessionKit.login({
        walletPlugin: WALLET_PLUGIN_IDS.cloudwallet,
      });

      if (response.session) {
        this.session = response.session;
        clientLogger.info("Cloud Wallet account created successfully", {
          accountName: response.session.actor.toString()
        });
        return this.buildLoginResult(response.session, options);
      }

      clientLogger.warn("No session in response");
      return {
        success: false,
        error: "Account creation failed - no session returned",
      };
    } catch (error) {
      clientLogger.error("Account creation error", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Cloud Wallet account creation failed",
      };
    }
  }

  /**
   * Login with existing Cloud Wallet account
   */
  async loginWithCloudWallet(options?: CloudWalletOptions): Promise<WaxLoginResult> {
    // Cloud Wallet accounts only exist on mainnet
    if (this.config.network === "testnet") {
      return {
        success: false,
        error: "WAX Cloud Wallet is not available on testnet. Please use Anchor or Wombat wallet with a testnet account.",
      };
    }

    if (!this.sessionKit) {
      await this.initialize();
    }

    if (!this.sessionKit) {
      return {
        success: false,
        error: "SessionKit not initialized",
      };
    }

    try {
      clientLogger.info("Starting Cloud Wallet login", { options });

      // Use Cloud Wallet plugin directly
      const response = await this.sessionKit.login({
        walletPlugin: WALLET_PLUGIN_IDS.cloudwallet,
      });

      if (response.session) {
        this.session = response.session;
        clientLogger.info("Cloud Wallet login successful", {
          accountName: response.session.actor.toString()
        });
        return this.buildLoginResult(response.session, options);
      }

      return {
        success: false,
        error: "Login failed - no session returned",
      };
    } catch (error) {
      clientLogger.error("Cloud Wallet login error", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Cloud Wallet login failed",
      };
    }
  }

  /**
   * Login with Wharfkit modal showing specified wallets
   *
   * When all wallets are allowed, uses the main SessionKit.
   * When a subset is requested, creates a filtered SessionKit to control UI.
   */
  async login(options?: {
    allowedWallets?: WalletType[];
  }): Promise<WaxLoginResult> {
    // Validate wallet types FIRST, before initializing SessionKit
    let allowedWallets = options?.allowedWallets || ["cloudwallet", "anchor", "wombat"];

    // On testnet, Cloud Wallet is not available (accounts only exist on mainnet)
    const isTestnet = this.config.network === "testnet";
    if (isTestnet && allowedWallets.includes("cloudwallet")) {
      allowedWallets = allowedWallets.filter(w => w !== "cloudwallet");
      clientLogger.info("Cloud Wallet excluded on testnet - accounts only exist on mainnet");
    }

    // Filter to only valid wallet types
    const validWalletTypes: WalletType[] = ["cloudwallet", "anchor", "wombat"];
    const validAllowedWallets = allowedWallets.filter(w => validWalletTypes.includes(w));

    if (validAllowedWallets.length === 0) {
      return {
        success: false,
        error: "No wallet plugins available for the specified wallets",
      };
    }

    // Now initialize SessionKit if needed
    if (!this.sessionKit) {
      await this.initialize();
    }

    if (!this.sessionKit) {
      return {
        success: false,
        error: "SessionKit not initialized",
      };
    }

    try {

      clientLogger.info("Starting login", {
        allowedWallets: validAllowedWallets,
        network: this.config.network
      });

      // Check if we need all wallets or a subset
      const allWallets: WalletType[] = isTestnet
        ? ["anchor", "wombat"]
        : ["cloudwallet", "anchor", "wombat"];

      const needsAllWallets = validAllowedWallets.length === allWallets.length &&
        allWallets.every(w => validAllowedWallets.includes(w));

      let response;

      if (needsAllWallets) {
        // Use main SessionKit - shows all available wallets
        clientLogger.debug("Using main SessionKit with all wallets");
        response = await this.sessionKit.login();
      } else if (validAllowedWallets.length === 1) {
        // Single wallet - use it directly without showing selection UI
        const walletPlugin = WALLET_PLUGIN_IDS[validAllowedWallets[0]];
        clientLogger.debug("Using single wallet directly", { wallet: validAllowedWallets[0] });
        response = await this.sessionKit.login({ walletPlugin });
      } else {
        // Subset of wallets - create filtered SessionKit to control UI
        clientLogger.debug("Creating filtered SessionKit", { allowedWallets: validAllowedWallets });

        const walletPlugins = [];

        if (validAllowedWallets.includes("cloudwallet")) {
          walletPlugins.push(
            new WalletPluginCloudWallet({
              url: "https://www.mycloudwallet.com",
              loginTimeout: 900000,
              supportedChains: [this.config.chainId],
              mobileAppConnectConfig: buildMobileAppConnectConfig(),
              // `allowTemp` was removed in @wharfkit/wallet-plugin-cloudwallet
              // 1.6.x. Temporary-account support is handled natively by the
              // new My Cloud Wallet flow.
            })
          );
        }

        if (validAllowedWallets.includes("anchor")) {
          walletPlugins.push(new WalletPluginAnchor());
        }

        if (validAllowedWallets.includes("wombat")) {
          walletPlugins.push(new WalletPluginWombat());
        }

        // This check is now redundant since we validate above, but keeping for safety
        if (walletPlugins.length === 0) {
          throw new Error("No wallet plugins available for the specified wallets");
        }

        const filteredSessionKit = new SessionKit({
          appName: "Scavenger Hunt SaaS",
          chains: [
            {
              id: this.config.chainId,
              url: this.config.rpcEndpoint,
            },
          ],
          ui: new WebRenderer(),
          walletPlugins,
        }, {
          transactPlugins: [new TransactPluginAutoCorrect()],
        });

        response = await filteredSessionKit.login();
      }

      if (response.session) {
        this.session = response.session;
        clientLogger.info("Login successful", {
          accountName: response.session.actor.toString(),
          walletType: response.session.walletPlugin?.id
        });
        return this.buildLoginResult(response.session);
      }

      return {
        success: false,
        error: "Login failed - no session returned",
      };
    } catch (error) {
      clientLogger.error("Login error", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Login failed",
      };
    }
  }

  /**
   * Check if we're running on testnet
   */
  isTestnet(): boolean {
    return this.config.network === "testnet";
  }

  /**
   * Get the current network
   */
  getNetwork(): 'mainnet' | 'testnet' {
    return this.config.network;
  }

  /**
   * Login with a specific wallet type
   */
  async loginWithWallet(
    walletType: WalletType,
    options?: CloudWalletOptions
  ): Promise<WaxLoginResult> {
    // Cloud Wallet accounts only exist on mainnet
    if (walletType === "cloudwallet" && this.config.network === "testnet") {
      return {
        success: false,
        error: "WAX Cloud Wallet is not available on testnet. Please use Anchor or Wombat wallet with a testnet account.",
      };
    }

    if (!this.sessionKit) {
      await this.initialize();
    }

    if (!this.sessionKit) {
      return {
        success: false,
        error: "SessionKit not initialized",
      };
    }

    try {
      clientLogger.info("Starting wallet login", { walletType, network: this.config.network });

      const walletPlugin = WALLET_PLUGIN_IDS[walletType];
      if (!walletPlugin) {
        throw new Error(`Unsupported wallet type: ${walletType}`);
      }

      const response = await this.sessionKit.login({
        walletPlugin,
      });

      if (response.session) {
        this.session = response.session;
        clientLogger.info("Wallet login successful", {
          accountName: response.session.actor.toString(),
          walletType
        });
        return this.buildLoginResult(response.session, options);
      }

      return {
        success: false,
        error: "Login failed - no session returned",
      };
    } catch (error) {
      clientLogger.error("Wallet login error", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Wallet login failed",
      };
    }
  }

  /**
   * Authenticate wallet (alias for login with all wallets)
   */
  async authenticateWallet(): Promise<{ success: boolean; accountName?: string; error?: string }> {
    const result = await this.login();
    return {
      success: result.success,
      accountName: result.userAccount?.accountName,
      error: result.error,
    };
  }

  // ============================================================================
  // Transaction Methods
  // ============================================================================

  /**
   * Execute a blockchain transaction
   */
  async transact(actions: TransactionAction[]): Promise<TransactionResult> {
    if (!this.session) {
      return {
        success: false,
        error: "No active session for transaction",
      };
    }

    try {
      const result = await this.session.transact({
        actions,
      });

      clientLogger.info("Transaction successful", { transactionId: result.response?.transaction_id });

      return {
        success: true,
        transactionId: result.response?.transaction_id,
      };
    } catch (error) {
      clientLogger.error("Transaction error", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Transaction failed",
      };
    }
  }

  // ============================================================================
  // Message Signing Methods (for identity verification)
  // ============================================================================

  /**
   * Sign a message for identity verification
   *
   * Uses the current wallet session to sign an arbitrary message.
   * This is used for challenge-response authentication to prove
   * ownership of a WAX account.
   *
   * @param message - Message to sign
   * @returns Signature result with signature string or error
   */
  async signMessage(message: string): Promise<{
    success: boolean;
    signature?: string;
    publicKey?: string;
    error?: string;
  }> {
    if (!this.session) {
      return {
        success: false,
        error: 'No active session - please login first'
      };
    }

    try {
      clientLogger.info("Signing message for identity verification");

      // Convert message to bytes (kept for potential future use)
      const _messageBytes = new TextEncoder().encode(message);

      // Use the session's wallet plugin to sign
      // Different wallets may implement this differently
      const walletPlugin = this.session.walletPlugin;

      // Check if the wallet supports direct message signing
      // Note: Not all wallets support signArbitrary - Cloud Wallet uses OAuth instead
      if (walletPlugin.id === WALLET_PLUGIN_IDS.cloudwallet) {
        // Cloud Wallet users should use OAuth for identity verification
        clientLogger.warn("Cloud Wallet does not support arbitrary message signing - use OAuth flow");
        return {
          success: false,
          error: 'Cloud Wallet identity verification should use OAuth. Please re-authenticate.'
        };
      }

      // For other wallets (Anchor, Wombat), we can attempt to sign
      // through the session's transact mechanism with a null transaction

      // Get the session's private key capability if available
      const metadata = walletPlugin.metadata;

      // Attempt to use the signing capability
      // This creates a signing request that the wallet will process
      try {
        // Create a minimal identity transaction for signing
        // This is the standard EOSIO way to prove identity without broadcasting
        const { SigningRequest } = await import('@wharfkit/signing-request');

        // Get account and permission as strings for the identity request
        const accountName = this.session.actor.toString();
        const permissionName = this.session.permission.toString();

        // Create an identity request with a callback (required by the API)
        // The callback URL is just for the request structure - we handle verification ourselves
        // Scope identifies the application requesting identity verification
        const request = SigningRequest.identity(
          {
            chainId: this.session.chain.id,
            account: accountName,
            permission: permissionName,
            scope: 'scavhunt', // Short scope name (max 12 chars, EOSIO name format)
            callback: {
              url: `${window.location.origin}/api/wax/authenticate`,
              background: true,
            },
          }
        );

        // Sign the request without broadcasting
        const result = await this.session.transact(
          { request },
          { broadcast: false }
        );

        if (result.signatures && result.signatures.length > 0) {
          const signature = result.signatures[0].toString();
          const publicKey = metadata.publicKey?.toString();

          clientLogger.info("Message signed successfully", {
            hasSignature: true,
            hasPublicKey: !!publicKey
          });

          return {
            success: true,
            signature,
            publicKey
          };
        }

        return {
          success: false,
          error: 'No signature returned from wallet'
        };

      } catch (signError) {
        clientLogger.warn("Standard signing failed, attempting alternative method", {
          error: signError instanceof Error ? signError.message : 'Unknown error'
        });

        // Alternative: Some wallets may expose direct signing
        // This is wallet-specific and may not be available
        return {
          success: false,
          error: 'Wallet does not support message signing. Please use OAuth verification.'
        };
      }

    } catch (error) {
      clientLogger.error("Message signing error", {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to sign message'
      };
    }
  }

  /**
   * Request and sign a verification challenge from the server
   *
   * This is the high-level method for verifying WAX account ownership:
   * 1. Requests a challenge from the server
   * 2. Signs the challenge with the wallet
   * 3. Submits the signature for verification
   *
   * @returns Verification result
   */
  async verifyAccountOwnership(): Promise<{
    success: boolean;
    error?: string;
  }> {
    const accountName = this.getCurrentUser();
    if (!accountName) {
      return { success: false, error: 'No active WAX session' };
    }

    try {
      // Step 1: Request a challenge
      const challengeResponse = await fetch('/api/wax/authenticate', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!challengeResponse.ok) {
        const errorData = await challengeResponse.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || 'Failed to get verification challenge'
        };
      }

      const challengeData = await challengeResponse.json();
      const { nonce, message } = challengeData;

      // Step 2: Sign the challenge
      const signResult = await this.signMessage(message);
      if (!signResult.success) {
        return { success: false, error: signResult.error };
      }

      // Step 3: Submit the signed challenge
      const verifyResponse = await fetch('/api/wax/authenticate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nonce,
          signature: signResult.signature,
          accountName
        }),
      });

      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || 'Verification failed'
        };
      }

      const verifyData = await verifyResponse.json();
      return {
        success: verifyData.success,
        error: verifyData.error
      };

    } catch (error) {
      clientLogger.error("Account ownership verification error", {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Verification failed'
      };
    }
  }

  // ============================================================================
  // Account Validation Methods
  // ============================================================================

  /**
   * Check if an account name is available (via server-side proxy)
   */
  async isAccountAvailable(accountName: string): Promise<boolean> {
    try {
      const response = await fetch(
        `/api/wax/check-account?account=${encodeURIComponent(accountName)}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      clientLogger.debug("Account availability check result", {
        accountName,
        available: data.available,
        exists: data.exists,
        network: data.network,
        rpcEndpoint: data.rpcEndpoint,
        clientNetwork: this.config.network,
      });

      return data.available;
    } catch (error) {
      clientLogger.error("Error checking account availability", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return false;
    }
  }

  /**
   * Check if an account exists on the blockchain (via server-side proxy)
   */
  async accountExists(accountName: string): Promise<boolean> {
    try {
      clientLogger.debug("Checking account existence", { accountName });

      const response = await fetch("/api/wax/validate-account", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ accountName }),
      });

      if (response.ok) {
        const data = await response.json();
        clientLogger.debug("Validation response", { accountName, exists: data.exists });
        return data.exists;
      } else {
        const errorData = await response.json().catch(() => ({}));
        clientLogger.debug("Validation failed", { accountName, errorData });

        if (response.status === 503) {
          clientLogger.warn("Network issues, allowing authentication to proceed");
          return true;
        }

        return false;
      }
    } catch (error) {
      clientLogger.error("Error checking account existence", {
        accountName,
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return true;
    }
  }

  /**
   * Validate that an account exists
   */
  async validateAccountExists(accountName: string): Promise<boolean> {
    const available = await this.isAccountAvailable(accountName);
    return !available;
  }

  // ============================================================================
  // Account Name Utilities
  // ============================================================================

  /**
   * Validate WAX Cloud Wallet account name format
   */
  isValidAccountName(accountName: string): boolean {
    const regex = /^[a-z1-5]{1,8}\.wam$/;
    return regex.test(accountName);
  }

  /**
   * Generate a WAX Cloud Wallet account name (ends with .wam)
   */
  generateAccountName(prefix: string = ""): string {
    const chars = "abcdefghijklmnopqrstuvwxyz12345";
    let result = prefix
      .toLowerCase()
      .replace(/[^a-z1-5]/g, "")
      .substring(0, 8);

    while (result.length < 8) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return result + ".wam";
  }

  /**
   * Check if current user has a temporary account
   */
  isTemporaryAccount(): boolean {
    const accountName = this.getCurrentUser();
    return accountName ? accountName.endsWith(".wam") : false;
  }
}
